import React, { Fragment, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import Footer from '../footer/Footer';

const portalElement: Element = document.getElementById('overlays') as Element;
const appElement: Element = document.getElementById('root') as Element;

const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
	onClose: () => void;
	ariaLabel?: string;
	children?: React.ReactNode;
}

const Modal = ({ onClose, ariaLabel, children }: ModalProps) => {
	const dialogRef = useRef<HTMLDivElement>(null);
	const previouslyFocused = useRef<HTMLElement | null>(null);

	useEffect(() => {
		previouslyFocused.current = document.activeElement as HTMLElement;
		const app = appElement as HTMLElement;
		app.style.overflowY = 'hidden';
		app.setAttribute('aria-hidden', 'true');

		const dialog = dialogRef.current;
		const focusables = dialog?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
		if (focusables && focusables.length > 0) {
			focusables[0].focus();
		} else {
			dialog?.focus();
		}

		return () => {
			app.style.overflowY = 'auto';
			app.removeAttribute('aria-hidden');
			previouslyFocused.current?.focus?.();
		};
	}, []);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.stopPropagation();
				onClose();
				return;
			}

			if (event.key !== 'Tab') return;

			const dialog = dialogRef.current;
			if (!dialog) return;

			const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
				(element) => element.offsetParent !== null
			);
			if (focusables.length === 0) {
				event.preventDefault();
				dialog.focus();
				return;
			}

			const first = focusables[0];
			const last = focusables[focusables.length - 1];
			const active = document.activeElement as HTMLElement;

			if (event.shiftKey) {
				if (active === first || !dialog.contains(active)) {
					event.preventDefault();
					last.focus();
				}
			} else if (active === last || !dialog.contains(active)) {
				event.preventDefault();
				first.focus();
			}
		},
		[onClose]
	);

	const overlay = (
		<div
			className="modal"
			role="dialog"
			aria-modal="true"
			aria-label={ariaLabel}
			ref={dialogRef}
			tabIndex={-1}
			onKeyDown={handleKeyDown}
		>
			<div className="modal-content">{children}</div>
			<Footer />
		</div>
	);

	return (
		<Fragment>
			{ReactDOM.createPortal(<div className="backdrop" onClick={onClose} />, portalElement)}
			{ReactDOM.createPortal(overlay, portalElement)}
		</Fragment>
	);
};

export default Modal;
